// Single shared cursor-following target reticle for the WHOLE app. There is
// only ever ONE on screen — the home gun-aim crosshair OR the adventure stance
// indicator, never both (they're mutually exclusive contexts). A module
// singleton enforces that physically: showing one style repaints the same
// element, so red can never stack on yellow.
//
// API:
//   • showReticle(style)  — paint + reveal, start following the cursor
//   • paintReticle(style) — repaint in place IF currently shown (mode switch)
//   • hideReticle()       — hide + stop following
// `style` = { color, variant, ring }:
//   • variant 'crosshair' → segments from each edge toward center, center gap
//   • variant 'ticks'     → short marks at N/E/S/W pointing inward
//   • ring 'solid' | 'dashed'

const SIZE = 70         // 50% smaller than the original 140
const CENTER_GAP = 7    // crosshair break at the center
const TICK_LEN = 8      // inward-tick length for the 'ticks' variant
const LINE = 1.5        // segment thickness

let el = null
let following = false
let hideTimer = null

// One absolutely-positioned line segment. `pos` keys are camelCase CSS props.
function addSegment(parent, color, pos) {
  const seg = document.createElement('div')
  let css = `position:absolute;background:${color};box-shadow:0 0 0 1px rgba(0,0,0,0.6);`
  for (const [k, v] of Object.entries(pos)) {
    css += `${k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}:${v};`
  }
  seg.style.cssText = css
  parent.appendChild(seg)
}

function ensure() {
  if (el) return el
  el = document.createElement('div')
  el.style.cssText = [
    'position:fixed', 'left:0', 'top:0',
    `width:${SIZE}px`, `height:${SIZE}px`,
    `margin-left:-${SIZE / 2}px`, `margin-top:-${SIZE / 2}px`,
    'pointer-events:none', 'z-index:9999', 'display:none', 'border-radius:50%',
    'opacity:1', 'transition:opacity 0.45s ease',
  ].join(';')
  document.body.appendChild(el)
  window.addEventListener('mousemove', onMove)
  window.addEventListener('touchmove', onTouch, { passive: true })
  return el
}
const at = (x, y) => { el.style.left = x + 'px'; el.style.top = y + 'px' }
function onMove(e) { if (following && el) at(e.clientX, e.clientY) }
function onTouch(e) { const t = e.touches?.[0]; if (t && following && el) at(t.clientX, t.clientY) }

function paint({ color, variant = 'crosshair', ring = 'solid' }) {
  ensure()
  el.style.border = `2px ${ring} ${color}`
  el.style.boxShadow = `0 0 0 1px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(0,0,0,0.6), 0 0 8px ${color}99`
  el.innerHTML = ''
  const half = SIZE / 2
  if (variant === 'ticks') {
    // Short ticks anchored at the ring edge, pointing inward.
    addSegment(el, color, { top: '0', left: '50%', width: `${LINE}px`, height: `${TICK_LEN}px`, marginLeft: `-${LINE / 2}px` })
    addSegment(el, color, { bottom: '0', left: '50%', width: `${LINE}px`, height: `${TICK_LEN}px`, marginLeft: `-${LINE / 2}px` })
    addSegment(el, color, { left: '0', top: '50%', height: `${LINE}px`, width: `${TICK_LEN}px`, marginTop: `-${LINE / 2}px` })
    addSegment(el, color, { right: '0', top: '50%', height: `${LINE}px`, width: `${TICK_LEN}px`, marginTop: `-${LINE / 2}px` })
  } else {
    // Full crosshair — segments from each edge toward center, gap at middle.
    const len = half - CENTER_GAP
    addSegment(el, color, { left: '0', top: '50%', height: `${LINE}px`, width: `${len}px`, marginTop: `-${LINE / 2}px` })
    addSegment(el, color, { right: '0', top: '50%', height: `${LINE}px`, width: `${len}px`, marginTop: `-${LINE / 2}px` })
    addSegment(el, color, { top: '0', left: '50%', width: `${LINE}px`, height: `${len}px`, marginLeft: `-${LINE / 2}px` })
    addSegment(el, color, { bottom: '0', left: '50%', width: `${LINE}px`, height: `${len}px`, marginLeft: `-${LINE / 2}px` })
  }
  // Center dot — exact aim point.
  const dot = document.createElement('div')
  dot.style.cssText = `position:absolute;left:50%;top:50%;width:5px;height:5px;margin:-2.5px 0 0 -2.5px;background:${color};border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,0.6);`
  el.appendChild(dot)
}

export function showReticle(style) {
  paint(style)
  following = true
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null }
  el.style.display = 'block'
  // next frame so the opacity transition runs if we were mid-fade-out
  requestAnimationFrame(() => { if (el) el.style.opacity = '1' })
}
export function paintReticle(style) { if (following) paint(style) }
// Fade out, then hide (the 30s auto-disarm + manual toggle-off both land here).
export function hideReticle() {
  following = false
  if (!el) return
  el.style.opacity = '0'
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => { if (el) el.style.display = 'none'; hideTimer = null }, 450)
}
